import { Box, Container, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import useCommonMediaQuery from '../../hooks/useCommonMediaQuery';

const PagePanel = ({
  breadcrumb,
  children,
  showWelcome = true,
  showBreadcrumb = true,
  backgroundColor = '#0D213B',
  contentBackgroundColor = 'white',
  contentBorderRadius = '16px 16px 16px 16px',
  tabs = null,
  activeTab = null,
  onTabChange = null,
  fixedContent = true,
  rightAction = null,
  boxShadow = '0 -4px 6px rgba(0,0,0,0.1)',
  internalScroll = true,
  contentTopOffset = '144px',
  headerExtra = null,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const isNarrowViewport = useCommonMediaQuery('sm');
  const isCompactTabsLayout = useCommonMediaQuery('lg');

  const rightActionWrapperSx = {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: 'fit-content',
    maxWidth: '100%',
    ml: 'auto',
    mr: { xs: 0, sm: 2 },
    '& > .MuiButton-root': {
      width: 'auto',
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f0f2f3',
        minHeight: '100vh',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: backgroundColor,
          color: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          position: internalScroll ? 'fixed' : '',
          top: '64px',
          left: 0,
          right: 0,
          zIndex: 1100,
          p: '18px',
          // Extra bottom padding when headerExtra is present so the content
          // card's 40px upward shift doesn't overlap it.
          pb: headerExtra ? '64px' : '36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="100%" sx={{ py: 0, px: { xs: 2, sm: 0 } }}>
          {showWelcome && location.pathname.startsWith('/dashboard') && (
            <Box sx={{ p: '20px 8px', position: 'relative', top: '15px' }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                  mt: -5,
                  fontSize: '1.25rem',
                }}
              >
                Welcome, {user?.fullName || 'User'}
              </Typography>
            </Box>
          )}
          {showBreadcrumb && (
            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  isNarrowViewport && !tabs ? 'flex-end' : 'space-between',
                alignItems: 'center',
                width: '100%',
                mb: tabs ? 0 : { xs: 1.5, md: 2, lg: 3 },
              }}
            >
              {!isNarrowViewport && (
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.875rem',
                  }}
                >
                  {breadcrumb}
                </Typography>
              )}
              {!tabs && rightAction && (
                <Box sx={{ ...rightActionWrapperSx, mt: -1 }}>
                  {rightAction}
                </Box>
              )}
            </Box>
          )}
          {tabs && (
            <Box sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isCompactTabsLayout ? 'stretch' : 'flex-end',
                  flexDirection: isCompactTabsLayout ? 'column-reverse' : 'row',
                  gap: isCompactTabsLayout ? 1 : 2,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0,
                    width: isCompactTabsLayout ? '100%' : 'fit-content',
                    maxWidth: '100%',
                    justifyContent: 'flex-start',
                    alignSelf: isCompactTabsLayout ? 'stretch' : 'auto',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarGutter: 'stable',
                    pb: '2px',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                      height: 0,
                      width: 0,
                    },
                  }}
                >
                  {tabs.map(tab => (
                    <Box
                      key={tab.value}
                      onClick={() =>
                        onTabChange && onTabChange(null, tab.value)
                      }
                      sx={{
                        px: 3,
                        py: 2,
                        minWidth: 'fit-content',
                        cursor: 'pointer',
                        borderBottom:
                          activeTab === tab.value
                            ? '2px solid #F1AC2F'
                            : '2px solid transparent',
                        color:
                          activeTab === tab.value
                            ? '#ffffff'
                            : 'rgba(255,255,255,0.7)',
                        fontWeight: activeTab === tab.value ? 600 : 400,
                        fontSize: '0.875rem',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          color: '#ffffff',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      {tab.label}
                    </Box>
                  ))}
                </Box>
                {rightAction && (
                  <Box
                    sx={{
                      ...rightActionWrapperSx,
                      pb: 1,
                      width: 'auto',
                      maxWidth: '100%',
                      alignSelf: isCompactTabsLayout ? 'flex-end' : 'auto',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {rightAction}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {headerExtra && <Box sx={{ mt: tabs ? 0 : 1 }}>{headerExtra}</Box>}
        </Container>
      </Box>
      {/* Main Content Container */}
      <Box
        sx={{
          position: fixedContent && internalScroll ? 'fixed' : 'relative',
          top: fixedContent && internalScroll ? contentTopOffset : 'auto',
          bottom: fixedContent && !internalScroll ? '40px' : '0px',
          left: fixedContent ? '50%' : 'auto',
          transform: fixedContent ? 'translateX(-50%)' : 'none',
          // Full-bleed on mobile; 95% (centered) from tablet up.
          width: { xs: '100%', sm: '95%' },
          maxWidth: '100%',
          minHeight: '75vh',
          zIndex: fixedContent ? 1200 : 'auto',
          backgroundColor: contentBackgroundColor,
          borderRadius: contentBorderRadius,
          boxShadow: fixedContent ? boxShadow : '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          mt: fixedContent ? 0 : '144px',
          overflow: internalScroll ? 'hidden' : 'unset',
          // paddingBottom: !internalScroll ? '10px' : '0px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PagePanel;
